import { Component, computed, effect, OnDestroy, OnInit } from '@angular/core';
import { MatDialogRef } from '@angular/material/dialog';
import { select, Store } from '@ngxs/store';
import { Subscription } from 'rxjs';
import { getDepartmentId } from 'src/app/services/store-snapshot.service';
import { IdName } from 'src/app/shared/models/id-name';
import { IDepartmentInfo } from 'src/app/shared/user-view/models/tenant-user-access.model';
import { ChangeCurrentSelectedDepartment } from 'src/app/tenant-selector/state/tenant-user-access.actions';
import {
  TenantUserAccessState,
  ALL_DEPS_TOKEN,
} from 'src/app/tenant-selector/state/tenant-user-access.state';
import { UnsavedDataGuard } from 'src/app/shared/guards/unsaved-data.guard';
import { GlobalSearchForm } from '../../forms/global-search-form';

@Component({
  selector: 'app-department-selector-mobile',
  templateUrl: './department-selector-mobile.component.html',
  styleUrls: ['./department-selector-mobile.component.scss'],
  standalone: false,
})
export class DepartmentSelectorMobileComponent implements OnInit, OnDestroy {
  allDepartmentsFromStore = select(TenantUserAccessState.getAllAvailableDepartments);
  selectedDepartment = select(TenantUserAccessState.currentDepartment);
  departmentListSorted = computed(() =>
    [...(this.allDepartmentsFromStore() ?? [])].sort((a, b) => a.name.localeCompare(b.name))
  );

  allDepsIconPath = '/assets/icons/all-departments.svg';
  departmentPlaceHolderIconPath = '/assets/icons/department-placeholder.svg';
  searchableDepartments: IdName[] = [];
  filteredDepartments: IDepartmentInfo[] = [];
  allAvailableDepartments: IDepartmentInfo[] = [];
  currentDepartmentId = getDepartmentId();
  form = new GlobalSearchForm();

  readonly allDepsToken = ALL_DEPS_TOKEN;

  private subscription = new Subscription();

  constructor(
    private dialogRef: MatDialogRef<DepartmentSelectorMobileComponent>,
    private store: Store,
    private unsavedDataGuard: UnsavedDataGuard
  ) {
    effect(() => {
      const availableDepartments = this.departmentListSorted();
      if (!availableDepartments?.length) {
        return;
      }
      this.filteredDepartments = availableDepartments;
      this.allAvailableDepartments = availableDepartments;
      this.searchableDepartments = availableDepartments.map((tenant: IDepartmentInfo) => ({
        id: tenant.id,
        name: tenant.name,
      }));
    });
  }

  ngOnInit(): void {
    this.subscription.add(
      this.form.controls.searchTerm.valueChanges.subscribe((searchTerm: string) => {
        this.filteredDepartments = this.allAvailableDepartments;
        if (searchTerm) {
          this.filteredDepartments = this.filteredDepartments.filter(
            (department: IDepartmentInfo) =>
              department.name.toLowerCase().includes(searchTerm.toLowerCase())
          );
        }
      })
    );
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }

  async selectDepartment(departmentId: string): Promise<void> {
    const canProceed = await this.unsavedDataGuard.checkUnsavedDataInCurrentComponentAsync();
    if (!canProceed) {
      return;
    }

    this.store.dispatch(new ChangeCurrentSelectedDepartment(departmentId, true));
    this.dialogRef.close();
  }

  closeDialog(): void {
    this.dialogRef.close();
  }
}
